<script>
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';
export default {
  components: {
    Spinner,
  },
  props: {
    initialFileName: {
      type: String,
      default: '',
    },
    isToShowFileNameOnUpload: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 100,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      uploadState: 'idle',
      label: this.$t('AUTOMATION.ATTACHMENT.LABEL_IDLE'),
    };
  },
  mounted() {
    if (this.initialFileName) {
      this.label = this.initialFileName;
      this.uploadState = 'uploaded';
    }
  },
  methods: {
    async onChangeFile(event) {
      const file = event.target.files[0];

      if (!file) return;

      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
      const MAX_VIDEO_SIZE = 16 * 1024 * 1024;
      const MAX_FILE_SIZE = this.size * 1024 * 1024;

      let errorMessage = '';

      if (isImage) {
        if (!['image/jpeg', 'image/png'].includes(file.type.toLowerCase())) {
          errorMessage = this.$t('AUTOMATION.ATTACHMENT.INVALID_IMAGE_TYPE');
        } else if (file.size > MAX_IMAGE_SIZE) {
          errorMessage = this.$t('AUTOMATION.ATTACHMENT.IMAGE_TOO_LARGE', {
            size: 5,
          });
        }
      } else if (isVideo) {
        if (!['video/mp4', 'video/3gpp'].includes(file.type.toLowerCase())) {
          errorMessage = this.$t('AUTOMATION.ATTACHMENT.INVALID_VIDEO_TYPE');
        } else if (file.size > MAX_VIDEO_SIZE) {
          errorMessage = this.$t('AUTOMATION.ATTACHMENT.VIDEO_TOO_LARGE', {
            size: 16,
          });
        }
      } else if (file.size > MAX_FILE_SIZE) {
        errorMessage = this.$t('AUTOMATION.ATTACHMENT.FILE_TOO_LARGE', {
          size: this.size,
        });
      }

      if (errorMessage) {
        useAlert(errorMessage);
        this.uploadState = 'failed';
        this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOAD_FAILED');
        return;
      }

      this.uploadState = 'processing';
      this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADING');

      try {
        const id = await this.$store.dispatch(
          'automations/uploadAttachment',
          file
        );
        this.$emit('update:modelValue', [id]);
        this.uploadState = 'uploaded';
        this.label = this.isToShowFileNameOnUpload
          ? file.name
          : this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADED');
      } catch (error) {
        this.uploadState = 'failed';
        this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOAD_FAILED');
        const apiErrorMessage =
          error?.response?.data?.error ||
          this.$t('AUTOMATION.ATTACHMENT.UPLOAD_ERROR');
        useAlert(apiErrorMessage);
      }
    },
  },
};
</script>

<template>
  <label class="input-wrapper" :class="uploadState">
    <input
      v-if="uploadState !== 'processing'"
      type="file"
      name="attachment"
      accept="image/jpeg,image/png,video/3gpp,video/mp4,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf"
      :class="uploadState === 'processing' ? 'disabled' : ''"
      @change="onChangeFile"
    />
    <Spinner v-if="uploadState === 'processing'" />
    <fluent-icon v-if="uploadState === 'idle'" icon="file-upload" />
    <fluent-icon
      v-if="uploadState === 'uploaded'"
      icon="checkmark-circle"
      type="outline"
      class="success-icon"
    />
    <fluent-icon
      v-if="uploadState === 'failed'"
      icon="dismiss-circle"
      type="outline"
      class="error-icon"
    />
    <p class="file-button">{{ label }}</p>
  </label>
</template>

<style scoped>
input[type='file'] {
  @apply hidden;
}
.input-wrapper {
  @apply flex h-9 bg-n-background dark:bg-n-solid-2 text-n-slate-12 dark:text-n-slate-12 py-1 px-2 items-center text-xs cursor-pointer rounded-sm border border-dashed border-n-strong dark:border-n-strong;
}
.success-icon {
  @apply text-n-teal-9 mr-2;
}
.error-icon {
  @apply text-n-ruby-9 mr-2;
}

.processing {
  @apply cursor-not-allowed opacity-90;
}
.file-button {
  @apply whitespace-nowrap overflow-hidden text-ellipsis w-full mb-0;
}
</style>
